import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MdAdd } from 'react-icons/md';

const predefinedColors = [
  '#FFC2D1', // Pink
  '#FFB3BA', // Rose
  '#FFDFBA', // Peach
  '#BAFFC9', // Mint
  '#BAE1FF', // Sky
  '#D0D1FF', // Lavender
  '#F0E68C', // Khaki

  '#F472B6', // Pink (Tailwind)
  '#F87171', // Red
  '#F5A623', // Amber
  '#34D399', // Green
  '#60A5FA', // Blue
  '#A78BFA'  // Purple
];

const colorNameMap = {
  // Older pastels
  '#FFC2D1': 'Pink',
  '#FFB3BA': 'Rose',
  '#FFDFBA': 'Peach',
  '#BAFFC9': 'Mint',
  '#BAE1FF': 'Sky',
  '#D0D1FF': 'Lavender',
  '#F0E68C': 'Khaki',

  // New Tailwind-like colors
  '#F472B6': 'Bright Pink',
  '#F5A623': 'Amber',
  '#F87171': 'Red',
  '#34D399': 'Green',
  '#60A5FA': 'Blue',
  '#A78BFA': 'Purple'
};

const LabelColorSection = ({ initialColor, onColorChange }) => {
    const [labelColor, setLabelColor] = useState(initialColor || predefinedColors[0]);

    useEffect(() => {
        if (initialColor) {
            setLabelColor(initialColor);
        }
    }, [initialColor]);

    const handleColorChange = (newColor, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        setLabelColor(newColor);
        if (onColorChange) {
            onColorChange(newColor);
        }
    };

    return (
        <Content>
            <Left>
                <SectionTitle>Selected Color</SectionTitle>
                <SelectedColorRowWrapper>
                    <SelectedColorRow>
                        <ColorPreview style={{ backgroundColor: labelColor }} />
                        <ColorInfo>
                            <ColorName>{colorNameMap[labelColor] || 'Custom'}</ColorName>
                            <ColorHex>{labelColor.toUpperCase()}</ColorHex>
                        </ColorInfo>
                    </SelectedColorRow>
                </SelectedColorRowWrapper>
            </Left>
            <Right>
                <SectionTitle>Colors</SectionTitle>
                <ColorGrid>
                    {predefinedColors.map((color) => (
                        <ColorCircle
                            key={color}
                            style={{
                                backgroundColor: color,
                                boxShadow: color === labelColor ? '0 0 0 3px white, 0 0 0 6px rgb(83, 74, 245)' : 'none'
                            }}
                            onClick={(e) => handleColorChange(color, e)}
                        />
                    ))}
                    <CustomLabel>
                        <MdAdd size={20} />
                        <HiddenInput
                            type="color"
                            value={labelColor}
                            onChange={(e) => handleColorChange(e.target.value, e)}
                        />
                    </CustomLabel>
                </ColorGrid>
            </Right>
            
        </Content>
    );
};

const SelectedColorRowWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;      // vertical center
  justify-content: center;
`;

const Content = styled.div`
  display: flex;
  margin-top: 8px;
  margin-bottom: 8px;
  min-width: 0;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const Left = styled.div`
  width: 30%;
  padding-right: 1rem;
  border-right: 1px solid #e5e7eb;
  flex-direction: column;
  display: flex;
  min-width: 0;

  @media (max-width: 600px) {
    width: 100%;
    padding-right: 0;
    padding-bottom: 12px;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }
`;

const Right = styled.div`
  width: 50%;
  display: flex;
  padding-left: 2rem;
  justify-content: center;
  flex-direction: column;
  min-width: 0;

  @media (max-width: 600px) {
    width: 100%;
    padding-left: 0;
  }
`;

const SectionTitle = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 0.75rem;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1.5rem;
  justify-items: center;

  @media (max-width: 600px) {
    gap: 0.5rem;
  }
`;

const ColorCircle = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  cursor: pointer;

  @media (max-width: 600px) {
    width: 1.6rem;
    height: 1.6rem;
  }
`;

const CustomLabel = styled.label`
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background-color: #f3f4f6;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  &:hover {
    background-color: #e5e7eb;
  }

  @media (max-width: 600px) {
    width: 1.6rem;
    height: 1.6rem;
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
`;


const SelectedColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ColorPreview = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 9999px;
  flex-shrink: 0;
  border: 2px solid #e5e7eb;
`;

const ColorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ColorName = styled.span`
  color: #374151;
  font-weight: 500;
`;


const ColorHex = styled.span`
  font-family: monospace;
  font-size: 12px;
  color: #374151;
`;


export default LabelColorSection;